// System prompt for the workflow agent. Unlike a kernel's `agent`
// step (a tool-less single transform — see kernelRunner's
// AGENT_STEP_SYSTEM_PROMPT), a workflow agent DRIVES: it plans its own
// steps and uses a read-only toolset to pursue a fixed goal, ending by
// producing a deliverable. The control inverts — the agent is the
// spine, the deterministic tools (analytics_query, python, kernels,
// read-only MCP) are its toolkit.
//
// Two invariants this prompt has to carry, because v1 enforces them at
// the prompt level (the toolset also drops obvious writes, but MCP
// read-only hints aren't a hard boundary — see workflowTools):
//   1. READ-ONLY. The agent observes and analyses; it never acts.
//   2. TERMINATION. The run has a fixed end state; the agent must reach
//      it and stop, not wander.

export function buildWorkflowSystemPrompt({ deliverableKind = "render" } = {}) {
    const deliverableRules = deliverableKind === "structured"
        ? [
            "## Deliverable",
            "When you have achieved the goal, call `return_result` with your final structured result (it must match the required output schema). That call IS the deliverable — do not narrate it.",
        ]
        : [
            "## Deliverable",
            "Your deliverable is a RENDER. When you have achieved the goal:",
            "  1. Call `render_block` with the final chart/table spec + its inline data — this is the artifact the run is saved and compared by.",
            "  2. Then call `return_result` with a one-line `summary` of what the run found.",
            "Render the SAME shape every run (same `kind`, same fields) so the deliverable is comparable across runs over time — the run history lining these up is the whole point.",
        ];

    return [
        "You are a WORKFLOW agent: a saved, goal-directed analysis that runs headless (not in a chat) and is RECORDED as a run the user can replay and compare over time.",
        "",
        "You are given a fixed GOAL, a set of bound PARAMETERS, and (when the workflow is scoped) the active SCOPE you must constrain your analysis to. Plan and take whatever read-only steps get you to the goal, then emit your deliverable and STOP.",
        "",
        "## Hard rules",
        "1. READ-ONLY. You may query analytics, run python over the results, run saved kernels, and call read-only MCP tools (list/get/search/read). You must NEVER call any tool that creates, updates, deletes, posts, comments, assigns, or otherwise WRITES — even if such a tool appears available. If reaching the goal would require an action, do not take it: report that in your deliverable instead.",
        "2. GROUND EVERYTHING. Base conclusions only on data you actually fetched this run. Never invent ids, counts, or facts.",
        "3. STAY SCOPED. If a scope is provided, every analytics query must be constrained to it (filter to the given endpoint/cluster/etc.). Do not broaden beyond the scope.",
        "4. TERMINATE. Reach the goal in as few steps as possible, emit the deliverable, and stop. Do not keep exploring once the goal is met.",
        "",
        ...deliverableRules,
        "",
        "## Style",
        "Don't narrate for the user — there is no chat here. Your tool calls do the work and your deliverable is the answer. Keep any assistant text to terse internal planning.",
    ].join("\n");
}

// Assemble the per-run task text handed to the agent: the goal, the
// resolved parameters, and (if scoped) the active scope binding,
// followed by the workflow's own parameterised instruction.
export function buildWorkflowTask({ goal, promptText, params, scope }) {
    const lines = [`# Goal`, goal || "(no goal specified)", ""];
    if (params && Object.keys(params).length) {
        lines.push("# Parameters (bound for this run)", "```json", safeJson(params), "```", "");
    }
    if (Array.isArray(scope) && scope.length) {
        // Label AND id: prose reasons about the label, but the analytics
        // filter (and the scope guard checking for it) needs the id.
        const scopeText = scope
            .map((s) => {
                const label = s.label || s.id;
                const id = s.id != null && String(s.id) !== String(label) ? ` (id: ${s.id})` : "";
                return `${s.dimLabel || s.dim}=${label}${id}`;
            })
            .join(" / ");
        lines.push(
            "# Scope (constrain ALL analysis to this)",
            scopeText,
            "Every analytics query you run must be filtered to this scope. Do not analyse data outside it.",
            "Queries that never reference the scope's ids come back flagged with a `_scope_warning`; treat such results as unusable and re-run the query with the scope filter.",
            "",
        );
    }
    lines.push("# Instructions", promptText || "(no instructions)");
    return lines.join("\n");
}

function safeJson(value) {
    try { return JSON.stringify(value, null, 2); } catch { return String(value); }
}
